import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import moment from "moment";
import logo from '../icons/flexichain.png';
import salesIcon from '../icons/sales.png';
import pricingIcon from '../icons/pricing.png';
import customerIcon from '../icons/customer.png';
import operationsIcon from '../icons/operations.png';
import customsIcon from '../icons/custom.png';
import freightIcon from '../icons/freight.png';
import warehouseIcon from '../icons/warehouse.png';
import transportIcon from '../icons/transport.png';
import accountsIcon from '../icons/account.png';
import hrmsIcon from '../icons/hrms.png';
import stockIcon from '../icons/stock.png';
import {
  CartesianGrid, Cell, Line, LineChart,
  Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { adminMainUrl } from "../../Config";


const moduleCards = [
  { title: "Sales & CRM", description: "Manage leads, opportunities and customer relationships", icon: salesIcon, color: "#11a75a", linkColor: "#11a75a", borderColor: "#d9f0e4", arrowBg: "#e9f9ef", shadow: "0 12px 24px rgba(17, 167, 90, .22)", cardBg: "linear-gradient(135deg, #13AE56, #057B36)" },
  { title: "Pricing", description: "Manage pricing, quotes and rate management", icon: pricingIcon, color: "#126cf1", linkColor: "#126cf1", borderColor: "#dce8ff", arrowBg: "#e8f0ff", shadow: "0 12px 24px rgba(18, 108, 241, .22)", cardBg: "linear-gradient(135deg, #0685FE, #0050D1)" },
  { title: "Customer Management", description: "Manage customers, contacts and communication", icon: customerIcon, color: "#7b4fe0", linkColor: "#7b4fe0", borderColor: "#eadfff", arrowBg: "#f0eaff", shadow: "0 12px 24px rgba(123, 79, 224, .22)", cardBg: "linear-gradient(135deg, #8E62EC, #5730C0)" },
  { title: "Operations", description: "Manage operations and service activities", icon: operationsIcon, color: "#ff6515", linkColor: "#ff6515", borderColor: "#ffe7cf", arrowBg: "#fff0e8", shadow: "0 12px 24px rgba(255, 101, 21, .2)", cardBg: "linear-gradient(135deg, #FD7E03, #F76400)" },
  { title: "Customs Clearance", description: "Manage customs clearance and documentation", icon: customsIcon, color: "#11a8bd", linkColor: "#11a8bd", borderColor: "#d8f2f7", arrowBg: "#e9f9fc", shadow: "0 12px 24px rgba(17, 168, 189, .2)", cardBg: "linear-gradient(135deg, #17AAC0, #08809F)" },
  { title: "Freight Forwarding", description: "Manage shipments and freight forwarding", icon: freightIcon, color: "#126cf1", linkColor: "#126cf1", borderColor: "#dce8ff", arrowBg: "#e8f0ff", shadow: "0 12px 24px rgba(18, 108, 241, .22)", cardBg: "linear-gradient(135deg, #0685FE, #0050D1)" },
  { title: "Warehouse Management", description: "Manage inventory, stock and warehouse operations", icon: warehouseIcon, color: "#11a75a", linkColor: "#11a75a", borderColor: "#d9f0e4", arrowBg: "#e9f9ef", shadow: "0 12px 24px rgba(17, 167, 90, .22)", cardBg: "linear-gradient(135deg, #13AE56, #057B36)" },
  { title: "Transport / Logistics", description: "Manage transport, fleet and logistics", icon: transportIcon, color: "#7b4fe0", linkColor: "#7b4fe0", borderColor: "#eadfff", arrowBg: "#f0eaff", shadow: "0 12px 24px rgba(123, 79, 224, .22)", cardBg: "linear-gradient(135deg, #8E62EC, #5730C0)" },
  { title: "Accounts & Finance", description: "Manage accounting, finance and transactions", icon: accountsIcon, color: "#ff970a", linkColor: "#ff970a", borderColor: "#ffe7cf", arrowBg: "#fff3df", shadow: "0 12px 24px rgba(255, 151, 10, .2)", cardBg: "linear-gradient(135deg, #FEA302, #F78E01)" },
  { title: "HRMS", description: "Manage employees, payroll and HR operations", icon: hrmsIcon, color: "#ee2d68", linkColor: "#ee2d68", borderColor: "#ffdce8", arrowBg: "#ffe8f0", shadow: "0 12px 24px rgba(238, 45, 104, .2)", cardBg: "linear-gradient(135deg, #FC4D7A, #D82457)" },
];

const salesData = [
  { day: "Mon", thisWeek: 240, lastWeek: 150 },
  { day: "Tue", thisWeek: 320, lastWeek: 205 },
  { day: "Wed", thisWeek: 410, lastWeek: 280 },
  { day: "Thu", thisWeek: 315, lastWeek: 200 },
  { day: "Fri", thisWeek: 375, lastWeek: 230 },
  { day: "Sat", thisWeek: 470, lastWeek: 345 },
  { day: "Sun", thisWeek: 405, lastWeek: 285 },
];

const shipmentData = [
  { name: "Delivered", value: 35, color: "#1f7aff", text: "Shipments successfully delivered", icon: freightIcon, centerBg: "#e8f1ff", centerAccent: "#1f7aff" },
  { name: "In Transit", value: 31, color: "#22bf72", text: "Shipments on the way", icon: transportIcon, centerBg: "#e6faf2", centerAccent: "#22bf72" },
  { name: "Pending", value: 20, color: "#ff7a1a", text: "Awaiting pickup or processing", icon: warehouseIcon, centerBg: "#fff3e6", centerAccent: "#ff7a1a" },
  { name: "Customs Clearance", value: 14, color: "#8b5cf6", text: "Under customs clearance", icon: customsIcon, centerBg: "#f0eaff", centerAccent: "#8b5cf6" },
];

const baseActivities = [
  { title: "New order #...", time: "Loading...", icon: operationsIcon, color: "#11a75a", isDynamic: true },
  { title: "Shipment #SHP-5678 updated", time: "15 mins ago", icon: transportIcon, color: "#126cf1", isDynamic: false },
  { title: "Invoice #INV-9012 generated", time: "45 mins ago", icon: warehouseIcon, color: "#7b4fe0", isDynamic: false },
  { title: "New customer registered", time: "1 hour ago", icon: hrmsIcon, color: "#ff6515", isDynamic: true },
  { title: "Stock level updated", time: "2 hours ago", icon: stockIcon, color: "#11a8bd", isDynamic: false },
];

const useWindowWidth = () => {
  const getWidth = () => (typeof window === "undefined" ? 1440 : window.innerWidth);
  const [width, setWidth] = useState(getWidth);
  useEffect(() => {
    let t;
    const fn = () => { clearTimeout(t); t = setTimeout(() => setWidth(getWidth()), 100); };
    window.addEventListener("resize", fn);
    return () => { window.removeEventListener("resize", fn); clearTimeout(t); };
  }, []);
  return width;
};

const useSidebarWidth = () => {
  const [sidebarWidth, setSidebarWidth] = useState(0);

  useEffect(() => {
    const measure = () => {
      const sidebar = document.querySelector(".sidebar");
      if (sidebar) {
        setSidebarWidth(sidebar.getBoundingClientRect().width);
      } else {
        setSidebarWidth(0);
      }
    };

    measure();

    const sidebar = document.querySelector(".sidebar");
    let ro;
    if (sidebar) {
      ro = new ResizeObserver(measure);
      ro.observe(sidebar);
    }

    window.addEventListener("resize", measure);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  return sidebarWidth;
};

const getWeekDateRange = (period) => {
  const now = moment();
  let start, end;
  if (period === 'lastWeek') {
    start = now.clone().subtract(1, 'week').startOf('isoWeek');
    end = now.clone().subtract(1, 'week').endOf('isoWeek');
  } else {
    start = now.clone().startOf('isoWeek');
    end = now.clone().endOf('isoWeek');
  }
  return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`;
};

const createStyles = ({ isMobile, isTablet, sidebarWidth, screenWidth }) => {
  const isDesktop = !isMobile && !isTablet;

  const availableWidth = screenWidth - (isDesktop ? sidebarWidth : 0);

  const getCardColumns = () => {
    if (availableWidth < 480) return "1fr";
    if (availableWidth < 720) return "repeat(2, 1fr)";
    if (availableWidth < 1000) return "repeat(3, 1fr)";
    if (availableWidth < 1280) return "repeat(4, 1fr)";
    return "repeat(5, 1fr)";
  };

  return {
    page: {
      width: isDesktop ? `calc(100% - ${sidebarWidth}px)` : "100%",
      maxWidth: isDesktop ? `calc(100% - ${sidebarWidth}px)` : "100%",
      marginLeft: isDesktop ? `${sidebarWidth}px` : 0,
      minHeight: "100vh",
      padding: isMobile ? "0 12px" : 0,
      background: "#ffffff",
      color: "#0f1f3d",
      fontFamily: "Roboto",
      overflowX: "auto",
      boxSizing: "border-box",
      transition: "margin-left 0.3s ease, width 0.3s ease, max-width 0.3s ease",
    },
    navbar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
      minHeight: isMobile ? 60 : 65,
      marginTop: isMobile ? 0 : 10,
      padding: isMobile ? "8px 12px" : "0 16px",
      borderBottom: "1px solid #d3dde9",
      background: "#ffffff",
      boxSizing: "border-box",
      gap: 12,
    },
    logoWrap: {
      width: isMobile ? 140 : 256,
      height: isMobile ? 48 : 65,
      overflow: "hidden",
      display: "flex",
      alignItems: "center",
      flexShrink: 0,
    },
    logo: {
      width: "100%",
      height: "auto",
      maxHeight: isMobile ? 40 : 80,
      objectFit: "contain",
      objectPosition: "left center",
    },
    navRight: {
      display: "flex",
      alignItems: "center",
      gap: isMobile ? 8 : 12,
      flexShrink: 0,
    },
    navIconButton: {
      position: "relative",
      width: 28,
      height: 28,
      border: 0,
      padding: 0,
      background: "transparent",
      color: "#273a5b",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 16,
      cursor: "pointer",
    },
    badge: {
      position: "absolute",
      top: -6,
      right: -4,
      minWidth: 14,
      height: 14,
      padding: "0 3px",
      borderRadius: 8,
      background: "#ff365f",
      color: "#ffffff",
      fontSize: 9,
      lineHeight: "14px",
      fontWeight: 800,
    },
    userName: {
      fontSize: 10,
      fontWeight: 700,
      color: "#0f1f3d",
      whiteSpace: "nowrap",
    },
    avatar: {
      width: 38,
      height: 38,
      borderRadius: "50%",
      objectFit: "cover",
      flexShrink: 0,
    },
    dashboardFrame: {
      width: "100%",
      maxWidth: 1536,
      margin: isMobile ? "12px auto 0" : "20px auto 0",
      padding: isMobile ? "0 12px" : isTablet ? "0 16px" : "0 17px",
      background: "#ffffff",
      boxSizing: "border-box",
    },
    dashboardContainer: {
      width: "100%",
      background: "#ffffff",
      borderRadius: 16,
      border: "1px solid #e6edf4",
      boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
      overflow: "hidden",
      boxSizing: "border-box",
    },
    headerBar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
      minHeight: 65,
      padding: isMobile ? "14px 12px" : "0 16px",
      background: "#1a67eb",
      color: "#ffffff",
      boxSizing: "border-box",
      flexWrap: isMobile || isTablet ? "wrap" : "nowrap",
      gap: isMobile ? 12 : 10,
    },
    headerTitleGroup: {
      display: "flex",
      alignItems: "baseline",
      gap: 18,
      flexWrap: "wrap",
    },
    pageTitle: {
      margin: 0,
      fontSize: isMobile ? 22 : 28,
      lineHeight: 1,
      fontWeight: 800,
      whiteSpace: "nowrap",
    },
    pageSubtitle: {
      fontSize: 11,
      lineHeight: 1,
      color: "rgba(255,255,255,.82)",
      whiteSpace: "nowrap",
    },
    headerActions: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      flexWrap: isMobile ? "wrap" : "nowrap",
      width: isMobile ? "100%" : "auto",
      justifyContent: isMobile ? "space-between" : "flex-start",
      overflow: "visible",
    },
    dateButton: {
      height: 36,
      minWidth: isMobile ? "auto" : 250,
      flex: isMobile ? "1 1 156px" : "0 0 auto",
      padding: "0 12px",
      border: "1px solid #d9e3f2",
      borderRadius: 6,
      background: "#ffffff",
      color: "#10234a",
      fontSize: 12,
      fontWeight: 700,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      cursor: "pointer",
    },
    weekButton: {
      height: 36,
      minWidth: isMobile ? "auto" : 138,
      flex: isMobile ? "1 1 118px" : "0 0 auto",
      padding: "0 12px",
      border: "1px solid #d9e3f2",
      borderRadius: 6,
      background: "#ffffff",
      color: "#10234a",
      fontSize: 12,
      fontWeight: 700,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      cursor: "pointer",
    },
    refreshButton: {
      width: 36,
      height: 36,
      border: "1px solid #d9e3f2",
      borderRadius: 6,
      background: "#ffffff",
      color: "#043bd2",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      cursor: "pointer",
    },

    cardsGrid: {
      padding: isMobile ? "16px 12px" : isTablet ? "24px 18px 18px" : "32px 25px 22px",
      display: "grid",
      gridTemplateColumns: getCardColumns(),
      gap: isMobile ? 12 : "24px 21px",
      boxSizing: "border-box",
      transition: "grid-template-columns 0.3s ease",
    },

    moduleCard: {
      minHeight: isMobile ? "auto" : 170,
      padding: isMobile ? "12px" : "16px 8px 16px 16px",
      border: "1px solid #dfe7f1",
      borderRadius: 12,
      background: "#ffffff",
      display: "grid",
      gridTemplateColumns: isMobile ? "40px minmax(0, 1fr)" : "48px minmax(0, 1fr)",
      gap: isMobile ? 12 : 14,
      boxSizing: "border-box",
      wordBreak: "break-word",
    },
    moduleIcon: {
      width: isMobile ? 40 : 48,
      height: isMobile ? 40 : 48,
      borderRadius: 10,
      color: "#ffffff",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: isMobile ? 20 : 25,
    },
    moduleBody: {
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
    },
    moduleTitle: {
      margin: "0 0 4px",
      color: "#17181C",
      fontSize: isMobile ? 12 : 15,
      lineHeight: 1.25,
      fontWeight: 700,
      wordBreak: "break-word",
    },
    moduleDescription: {
      margin: "0 0 8px",
      color: "#25395d",
      fontSize: isMobile ? 11 : 13,
      lineHeight: 1.35,
      fontWeight: 400,
      overflow: "visible",
      whiteSpace: "normal",
      wordBreak: "break-word",
    },
    moduleLink: {
      marginTop: "auto",
      fontSize: isMobile ? 11 : 13,
      fontWeight: 500,
      lineHeight: 1,
      textDecoration: "none",
      display: "flex",
      alignItems: "center",
      justifyContent:"end",
      gap: 6,
      whiteSpace: "nowrap",
    },
    moduleArrow: {
      width: isMobile ? 20 : 22,
      height: isMobile ? 20 : 22,
      borderRadius: "50%",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: isMobile ? 16 : 20,
    },
    chartsGrid: {
      padding: isMobile || isTablet ? "0 12px" : "0 25px",
      display: "grid",
      gridTemplateColumns: isMobile || isTablet ? "1fr" : "1.08fr 0.92fr",
      gap: 16,
      boxSizing: "border-box",
    },
    chartCard: {
      minHeight: isMobile ? 250 : 280,      // fixed min-height to prevent shrinking
      height: isMobile ? 250 : 280,         // explicit height to ensure chart has space
      padding: isMobile ? "12px" : "16px 18px 10px",
      border: "1px solid #dfe7f1",
      borderRadius: 12,
      background: "#ffffff",
      boxSizing: "border-box",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",                  // prevent overflow issues
    },
    chartHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      minHeight: 34,
      marginBottom: 8,
      gap: 10,
      flexWrap: isMobile ? "wrap" : "nowrap",
      flexShrink: 0,
    },
    sectionTitle: {
      margin: 0,
      color: "#10203e",
      fontSize: 15,
      fontWeight: 800,
    },
    chartControls: {
      display: "flex",
      alignItems: "center",
      gap: isMobile ? 8 : 24,
      flexWrap: "wrap",
    },
    legendText: {
      color: "#1f3152",
      fontSize: 11,
      fontWeight: 700,
      display: "inline-flex",
      alignItems: "center",
      gap: 7,
      whiteSpace: "nowrap",
    },
    legendBlue: {
      width: 22,
      height: 2,
      borderRadius: 4,
      background: "#1d63ff",
      display: "inline-block",
    },
    legendDashed: {
      width: 22,
      height: 0,
      borderTop: "2px dashed #6f8cc2",
      display: "inline-block",
    },
    smallDropdown: {
      height: 30,
      padding: "0 10px",
      border: "1px solid #d9e3f2",
      borderRadius: 6,
      background: "#ffffff",
      color: "#10234a",
      fontSize: 11,
      fontWeight: 700,
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      cursor: "pointer",
    },
    shipmentBody: {
      display: "flex",
      alignItems: "center",
      minHeight: isMobile ? "auto" : 184,
      height: "auto",
      flexDirection: isMobile ? "column" : "row",
      gap: isMobile ? 16 : 0,
      flex: 1,
    },
    statusList: {
      width: isMobile ? "100%" : "57%",
      display: "grid",
      gap: 12,
    },
    statusItem: {
      display: "grid",
      gridTemplateColumns: "12px minmax(0, 1fr) 42px",
      alignItems: "start",
      gap: 8,
    },
    statusDot: {
      width: 10,
      height: 10,
      marginTop: 4,
      borderRadius: "50%",
    },
    statusTitle: {
      display: "block",
      color: "#10203e",
      fontSize: 12,
      fontWeight: 800,
    },
    statusText: {
      display: "block",
      color: "#64718a",
      fontSize: 10,
    },
    statusValue: {
      color: "#10203e",
      fontSize: 13,
      textAlign: "right",
    },
    activityCard: {
      margin: isMobile || isTablet ? "16px 12px 12px" : "16px 25px 22px",
      padding: isMobile ? "12px" : "16px 18px",
      border: "1px solid #dfe7f1",
      borderRadius: 12,
      background: "#ffffff",
    },
    activityGrid: {
      marginTop: 14,
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : isTablet ? "repeat(2, 1fr)" : "repeat(5, 1fr)",
      gap: 12,
    },
    activityItem: {
      display: "grid",
      gridTemplateColumns: "34px minmax(0, 1fr)",
      alignItems: "center",
      gap: 10,
    },
    activityIcon: {
      width: 34,
      height: 34,
      borderRadius: "50%",
      color: "#ffffff",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 17,
    },
    activityTitle: {
      display: "block",
      color: "#302F53",
      fontSize: 12,
      fontWeight: 600,
      whiteSpace: "normal",
      wordBreak: "break-word",
    },
    activityTime: {
      display: "block",
      marginTop: 3,
      color: "#302F53",
      fontSize: 10,
      fontWeight: 400,
    },
    footer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
      minHeight: 71.19,
      marginTop: 22,
      padding: isMobile ? "12px" : "0px",
      borderTop: "1px solid #d3dde9",
      color: "#5b6a80",
      background: "#ffffff",
      fontSize: 12,
      flexWrap: "wrap",
      gap: isMobile ? 10 : 0,
    },
    footerLink: {
      color: "#0f63ef",
      fontWeight: 800,
      textDecoration: "none",
    },
  };
};


const getModuleRoute = (title) => {
  switch (title) {
    case "Sales & CRM": return "/flexichain/all-query-list";
    case "Pricing": return "/flexichain/pricing/air/import/rate-request/requested";
    case "Customer Management": return "/flexichain/all-customer-list";
    case "Freight Forwarding": return "/flexichain/job-order/freight-forwarding/air/import";
    case "Customs Clearance": return "/flexichain/job-order/custom-clearance/air/import";
    case "Transport / Logistics": return "/flexichain/transport-quote-list";
    default:
      const slug = title.toLowerCase().replace(/[ &/]+/g, '-').replace(/--+/g, '-').replace(/^-|-$/g, '');
      return `/flexichain/${slug}`;
  }
};

const ModuleCard = ({ card, styles }) => (
  <div style={{ ...styles.moduleCard, borderColor: card.borderColor }}>
    <div style={{ ...styles.moduleIcon, background: card.cardBg || card.color, boxShadow: card.shadow }}>
      <img src={card.icon} alt={card.title} style={{ width: 28, height: 28, objectFit: "contain" }} />
    </div>
    <div style={styles.moduleBody}>
      <h4 style={styles.moduleTitle}>{card.title}</h4>
      <p style={styles.moduleDescription}>{card.description}</p>
      <Link to={getModuleRoute(card.title)} style={{ ...styles.moduleLink, color: card.linkColor }}>
        View Details
        <span style={{ ...styles.moduleArrow, color: card.linkColor, background: card.arrowBg }}>
          <i className="ti ti-arrow-right" />
        </span>
      </Link>
    </div>
  </div>
);

const SalesOverview = ({ styles, isMobile, globalPeriod, loading }) => {
  const [activePeriod, setActivePeriod] = useState(globalPeriod);
  const [thisWeekData, setThisWeekData] = useState([]);
  const [lastWeekData, setLastWeekData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);

  const fetchPeriodData = async (period) => {
    const token = Cookies.get('token');
    const userId = Cookies.get('flexichain_user_id');
    try {
      const response = await axios.get(`${adminMainUrl}/api/saleOverview`, {
        params: { period },
        headers: { 'auth-token': token, 'user_id': userId, 'Content-Type': 'application/json' }
      });
      if (response.data.success && Array.isArray(response.data.data)) return response.data.data;
      return [];
    } catch (error) {
      console.error("Error fetching sales overview:", error);
      return [];
    }
  };

  useEffect(() => {
    const fetchBothWeeks = async () => {
      setIsLoading(true);
      const [thisWeek, lastWeek] = await Promise.all([fetchPeriodData('thisWeek'), fetchPeriodData('lastWeek')]);
      setThisWeekData(thisWeek);
      setLastWeekData(lastWeek);
      setIsLoading(false);
    };
    fetchBothWeeks();
  }, []);

  useEffect(() => { setActivePeriod(globalPeriod); }, [globalPeriod]);

  const handlePeriodSelect = (newPeriod) => { setShowDropdown(false); setActivePeriod(newPeriod); };

  const mergedData = thisWeekData.map((item, index) => ({
    day: item.day, thisWeek: item.value, lastWeek: lastWeekData[index]?.value || 0,
  }));

  const isThisWeekActive = activePeriod === 'thisWeek';

  return (
    <div style={styles.chartCard}>
      <div style={styles.chartHeader}>
        <h3 style={styles.sectionTitle}>Sales Overview</h3>
        <div style={styles.chartControls}>
          {!isMobile && (
            <>
              <span style={styles.legendText}>
                <span style={isThisWeekActive ? styles.legendBlue : { ...styles.legendBlue, background: "#6f8cc2" }} />
                This Week
              </span>
              <span style={styles.legendText}>
                <span style={!isThisWeekActive ? styles.legendBlue : { ...styles.legendBlue, background: "#6f8cc2" }} />
                Last Week
              </span>
            </>
          )}
          <div style={{ position: 'relative' }}>
            <button type="button" style={styles.smallDropdown} onClick={() => setShowDropdown(!showDropdown)}>
              {activePeriod === 'thisWeek' ? 'This Week' : 'Last Week'}
              <i className="ti ti-chevron-down" />
            </button>
            {showDropdown && (
              <div style={{ position: 'absolute', top: '100%', right: 0, background: '#fff', border: '1px solid #d9e3f2', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10, minWidth: '120px' }}>
                <div style={{ padding: '8px 12px', cursor: 'pointer', fontWeight: activePeriod === 'thisWeek' ? 700 : 400 }} onClick={() => handlePeriodSelect('thisWeek')}>This Week</div>
                <div style={{ padding: '8px 12px', cursor: 'pointer', fontWeight: activePeriod === 'lastWeek' ? 700 : 400, borderTop: '1px solid #eef2f7' }} onClick={() => handlePeriodSelect('lastWeek')}>Last Week</div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>
        <ResponsiveContainer width="100%" height={isMobile ? 210 : 178}>
          {isLoading || loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>Loading...</div>
          ) : (
            <LineChart data={mergedData} margin={{ top: 8, right: 14, left: -16, bottom: 0 }}>
              <CartesianGrid stroke="#ecf1f7" strokeWidth={1} vertical={!isMobile} />
              <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "#1f3152" }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "#1f3152" }} />
              <Tooltip />
              <Line type="monotone" dataKey="thisWeek" name="This Week" stroke="#1d63ff" strokeWidth={isThisWeekActive ? 3 : 2} strokeDasharray={isThisWeekActive ? "0" : "4 4"} dot={{ r: isThisWeekActive ? 4 : 2, fill: "#1d63ff", strokeWidth: 0 }} opacity={isThisWeekActive ? 1 : 0.5} />
              <Line type="monotone" dataKey="lastWeek" name="Last Week" stroke="#6f8cc2" strokeWidth={!isThisWeekActive ? 3 : 2} strokeDasharray={!isThisWeekActive ? "0" : "4 4"} dot={{ r: !isThisWeekActive ? 4 : 2, fill: "#6f8cc2", strokeWidth: 0 }} opacity={!isThisWeekActive ? 1 : 0.5} />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const DonutCenterContent = ({ activeSegment, isMobile }) => {
  const innerR = isMobile ? 48 : 42;
  const active = activeSegment || shipmentData[0];
  return (
    <g>
      <circle cx="50%" cy="50%" r={innerR - 4} fill={active.centerBg} style={{ transition: "fill 0.3s ease" }} />
      <foreignObject x="50%" y="50%" width={isMobile ? 48 : 40} height={isMobile ? 48 : 40} style={{ transform: `translate(-${isMobile ? 24 : 20}px, -${isMobile ? 28 : 24}px)`, transition: "all 0.3s ease", overflow: "visible" }}>
        <div xmlns="http://www.w3.org/1999/xhtml" style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2 }}>
          <div style={{ width: isMobile ? 36 : 30, height: isMobile ? 36 : 30, borderRadius: "50%", background: active.centerAccent, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 3px 10px ${active.centerAccent}55` }}>
            <img src={active.icon} alt={active.name} style={{ width: isMobile ? 20 : 17, height: isMobile ? 20 : 17, objectFit: "contain", filter: "brightness(0) invert(1)" }} />
          </div>
          <span style={{ fontSize: isMobile ? 11 : 9, fontWeight: 800, color: active.centerAccent, lineHeight: 1 }}>{active.value}%</span>
        </div>
      </foreignObject>
    </g>
  );
};

const ShipmentStatus = ({ styles, isMobile }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeSegment, setActiveSegment] = useState(shipmentData[0]);

  const handleMouseEnter = (_, index) => { setActiveIndex(index); setActiveSegment(shipmentData[index]); };
  const handleMouseLeave = () => { setActiveIndex(0); setActiveSegment(shipmentData[0]); };

  return (
    <div style={styles.chartCard}>
      <div style={styles.chartHeader}>
        <h3 style={styles.sectionTitle}>Shipments by Status</h3>
        <button type="button" style={styles.smallDropdown}>This Week <i className="ti ti-chevron-down" /></button>
      </div>
      <div style={styles.shipmentBody}>
        <div style={{ width: isMobile ? "100%" : "43%", height: isMobile ? 210 : 184, position: "relative", flexShrink: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={shipmentData} dataKey="value" innerRadius={isMobile ? 48 : 42} outerRadius={isMobile ? 74 : 68} paddingAngle={2} startAngle={110} endAngle={470} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} style={{ cursor: "pointer", outline: "none" }} strokeWidth={0}>
                {shipmentData.map((entry, index) => (
                  <Cell key={entry.name} fill={entry.color} opacity={activeIndex === index ? 1 : 0.82} style={{ filter: activeIndex === index ? `drop-shadow(0 0 6px ${entry.color}88)` : "none", transition: "all 0.25s ease" }} />
                ))}
              </Pie>
              <DonutCenterContent activeSegment={activeSegment} isMobile={isMobile} />
              <Tooltip formatter={(value, name) => [`${value}%`, name]} contentStyle={{ borderRadius: 8, border: "none", boxShadow: "0 4px 16px rgba(0,0,0,0.12)", fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div style={styles.statusList}>
          {shipmentData.map((item, index) => (
            <div key={item.name} style={{ ...styles.statusItem, cursor: "pointer", padding: "4px 6px", borderRadius: 6, background: activeIndex === index ? `${item.color}12` : "transparent" }} onMouseEnter={() => { setActiveIndex(index); setActiveSegment(item); }} onMouseLeave={() => { setActiveIndex(0); setActiveSegment(shipmentData[0]); }}>
              <span style={{ ...styles.statusDot, background: item.color }} />
              <div>
                <strong style={{ ...styles.statusTitle, color: activeIndex === index ? item.color : "#10203e" }}>{item.name}</strong>
                <small style={styles.statusText}>{item.text}</small>
              </div>
              <b style={{ ...styles.statusValue, color: activeIndex === index ? item.color : "#10203e" }}>{item.value}%</b>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const RecentActivity = ({ styles, isMobile, isTablet, activities }) => (
  <section style={styles.activityCard}>
    <h3 style={styles.sectionTitle}>Recent Activity</h3>
    <div style={styles.activityGrid}>
      {activities.map((item, index) => (
        <div key={index} style={styles.activityItem}>
          <span style={{ ...styles.activityIcon, background: item.color }}>
            <img src={item.icon} alt={item.title} style={{ width: 18, height: 18, objectFit: "contain" }} />
          </span>
          <div style={{ minWidth: 0 }}>
            <strong style={styles.activityTitle}>{item.title}</strong>
            <small style={styles.activityTime}>{item.time}</small>
          </div>
        </div>
      ))}
    </div>
  </section>
);

const Dashboard2 = () => {
  const screenWidth = useWindowWidth();
  const isMobile = screenWidth < 768;
  const isTablet = screenWidth >= 768 && screenWidth < 1200;

  const sidebarWidth = useSidebarWidth();

  const styles = useMemo(
    () => createStyles({ isMobile, isTablet, sidebarWidth, screenWidth }),
    [isMobile, isTablet, sidebarWidth, screenWidth]
  );

  const [globalPeriod, setGlobalPeriod] = useState('thisWeek');
  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState(baseActivities);
  const [showTopWeekDropdown, setShowTopWeekDropdown] = useState(false);
  const [dateRange, setDateRange] = useState(getWeekDateRange('thisWeek'));

  useEffect(() => { setDateRange(getWeekDateRange(globalPeriod)); }, [globalPeriod]);

  const updateLatestJobOrder = async () => {
    const token = Cookies.get('token');
    const userId = Cookies.get('flexichain_user_id');
    try {
      const response = await axios.post(`${adminMainUrl}/api/jobOrder/latestJobOrder`, {}, {
        headers: { 'auth-token': token, 'user_id': userId, 'Content-Type': 'application/json' }
      });
      if (response.data.success && response.data.data) {
        const job = response.data.data;
        const timeAgo = moment(job.createdAt).fromNow();
        setActivities(prev => prev.map((act, idx) =>
          idx === 0 ? { ...act, title: `New order #${job.job_order_unique_no} created`, time: timeAgo } : act
        ));
      }
    } catch (error) { console.error("Error fetching latest job order:", error); }
  };

  const updateLatestCustomerTime = async () => {
    const token = Cookies.get('token');
    const userId = Cookies.get('flexichain_user_id');
    try {
      const response = await axios.get(`${adminMainUrl}/api/customer/latestlist`, {
        headers: { 'auth-token': token, 'user_id': userId, 'Content-Type': 'application/json' }
      });
      if (response.data.success && Array.isArray(response.data.data) && response.data.data.length > 0) {
        const timeAgo = moment(response.data.data[0].createdAt).fromNow();
        setActivities(prev => prev.map(act =>
          act.title === "New customer registered" ? { ...act, time: timeAgo } : act
        ));
      }
    } catch (error) { console.error("Error fetching latest customer:", error); }
  };

  const refreshAllData = () => { updateLatestJobOrder(); updateLatestCustomerTime(); };

  useEffect(() => { updateLatestJobOrder(); updateLatestCustomerTime(); }, []);

  const handleGlobalWeekSelect = (newPeriod) => { setShowTopWeekDropdown(false); setGlobalPeriod(newPeriod); };

  return (
    <div style={styles.page}>
      <header style={styles.navbar}>
        <div style={styles.logoWrap}>
          {/* <img src={logo} alt="Flexi Chain" style={styles.logo} /> */}
        </div>
        <div style={styles.navRight}>
          <button type="button" aria-label="notifications" style={styles.navIconButton}>
            <i className="ti ti-message" />
            <span style={styles.badge}>9</span>
          </button>
          <button type="button" aria-label="Settings" style={styles.navIconButton}>
            <i className="ti ti-settings" />
          </button>
          {!isMobile && <span style={styles.userName}>Michelle White</span>}
          <img src={logo} alt="Michelle White" style={styles.avatar} />
        </div>
      </header>

      <main style={styles.dashboardFrame}>
        <section style={styles.dashboardContainer}>
          <div style={styles.headerBar}>
            <div style={styles.headerTitleGroup}>
              <h1 style={styles.pageTitle}>Dashboard</h1>
              {!isMobile && <span style={styles.pageSubtitle}>Overview of your business operations</span>}
            </div>
            <div style={styles.headerActions}>
              <button type="button" style={styles.dateButton}>
                <i className="ti ti-calendar" />
                <span>{dateRange}</span>
                <i className="ti ti-chevron-down" />
              </button>
              <div style={{ position: 'relative' }}>
                <button type="button" style={styles.weekButton} onClick={() => setShowTopWeekDropdown(!showTopWeekDropdown)}>
                  <i className="ti ti-filter" />
                  <span>{globalPeriod === 'thisWeek' ? 'This Week' : 'Last Week'}</span>
                  <i className="ti ti-chevron-down" />
                </button>
                {showTopWeekDropdown && (
                  <div style={{ position: 'absolute', top: '100%', right: 0, background: '#fff', border: '1px solid #d9e3f2', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10, minWidth: '120px' }}>
                    <div style={{ padding: '8px 12px', cursor: 'pointer', fontWeight: globalPeriod === 'thisWeek' ? 700 : 400, color: '#000000' }} onClick={() => handleGlobalWeekSelect('thisWeek')}>This Week</div>
                    <div style={{ padding: '8px 12px', cursor: 'pointer', fontWeight: globalPeriod === 'lastWeek' ? 700 : 400, borderTop: '1px solid #eef2f7', color: '#000000' }} onClick={() => handleGlobalWeekSelect('lastWeek')}>Last Week</div>
                  </div>
                )}
              </div>
              <button type="button" aria-label="Refresh" style={styles.refreshButton} onClick={refreshAllData}>
                <i className="ti ti-refresh" />
              </button>
            </div>
          </div>

          <section style={styles.cardsGrid}>
            {moduleCards.map((card) => (
              <ModuleCard key={card.title} card={card} styles={styles} />
            ))}
          </section>

          <section style={styles.chartsGrid}>
            <SalesOverview styles={styles} isMobile={isMobile} globalPeriod={globalPeriod} loading={loading} />
            <ShipmentStatus styles={styles} isMobile={isMobile} />
          </section>

          <RecentActivity styles={styles} isMobile={isMobile} isTablet={isTablet} activities={activities} />
        </section>
      </main>

      <footer style={styles.footer}>
        <span>© 2026 <Link to="#" style={styles.footerLink}>Flexichain.</Link> All Rights Reserved.</span>
        <span>Made by <Link to="#" style={styles.footerLink}>Alobha Technologies</Link></span>
      </footer>
    </div>
  );
};

export default Dashboard2;